import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from './lib/api';
import WeeklyStaffingRequirements from "./WeeklyStaffingRequirements";
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Divider,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PresetHoursSettings from './PresetHoursSettings';
import PositionsSettings from './PositionsSettings';
import { usePresetHours } from './PresetHoursContext';
import axios from 'axios';

// Constants
const DAYS_OF_WEEK = [
  { value: 1, label: 'Δευτέρα' },
  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },
  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },
  { value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' }
];

const TIME_PERIODS = [
  { value: 'morning', label: 'Πρωί' },
  { value: 'afternoon', label: 'Απόγευμα' },
  { value: 'evening', label: 'Βράδυ' }
];

const POSITIONS = [
  { value: 'service', label: 'Σέρβις' },
  { value: 'buffet', label: 'Μπουφές' },
  { value: 'kitchen', label: 'Κουζίνα' },
  { value: 'bar', label: 'Μπαρ' }
];

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Store Hours Component
const StoreHoursSettings = () => {
  const [storeHours, setStoreHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchStoreHours();
  }, []);

  const fetchStoreHours = async () => {
    try {
      const response = await fetchWithAuth('/api/store-settings/store-hours');
      if (response.ok) {
        const data = await response.json();
        // Convert is_open from number to boolean
        const processedData = data.map(hour => ({
          ...hour,
          is_open: Boolean(hour.is_open)
        }));
        setStoreHours(processedData);
      }
    } catch (error) {
      setError('Σφάλμα κατά τη φόρτωση ωραρίου καταστήματος');
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (dayOfWeek, field, value) => {
    setStoreHours(prev => 
      prev.map(hour => 
        hour.day_of_week === dayOfWeek 
          ? { ...hour, [field]: value }
          : hour
      )
    );
  };

  const saveStoreHours = async () => {
    try {
      setLoading(true);
      for (const hour of storeHours) {
        const response = await fetchWithAuth('/api/store-settings/store-hours', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(hour)
        });
        if (!response.ok) throw new Error('Αποτυχία αποθήκευσης');
      }
      setSuccess('Το ωράριο αποθηκεύτηκε επιτυχώς!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Σφάλμα κατά την αποθήκευση');
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (dayOfWeek) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayOfWeek);
    return day ? day.label : dayOfWeek;
  };

  if (loading) return <Typography>Φόρτωση...</Typography>;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Ωράριο Καταστήματος
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={2}>
        {storeHours.map((hour) => (
          <Grid item xs={12} sm={6} md={4} key={hour.day_of_week}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {getDayLabel(hour.day_of_week)}
              </Typography>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={hour.is_open}
                    onChange={(e) => handleTimeChange(hour.day_of_week, 'is_open', e.target.checked)}
                  />
                }
                label="Ανοιχτό"
              />
              
              {hour.is_open === true && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    label="Άνοιγμα"
                    type="time"
                    value={hour.opening_time || ''}
                    onChange={(e) => handleTimeChange(hour.day_of_week, 'opening_time', e.target.value)}
                    fullWidth
                    sx={{ mb: 1 }}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="Κλείσιμο"
                    type="time"
                    value={hour.closing_time || ''}
                    onChange={(e) => handleTimeChange(hour.day_of_week, 'closing_time', e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          onClick={saveStoreHours}
          disabled={loading}
        >
          Αποθήκευση Ωραρίου
        </Button>
      </Box>
    </Box>
  );
};

// Staffing Requirements Component
const StaffingRequirementsSettings = () => {
  const [staffingRequirements, setStaffingRequirements] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    day_of_week: 1,
    time_period: 'morning',
    time_start: '09:00',
    time_end: '17:00',
    position: 'service',
    required_staff: 1,
    shift_duration_hours: 8
  });

  useEffect(() => {
    fetchStaffingRequirements();
  }, []);

  const fetchStaffingRequirements = async () => {
    try {
      const response = await fetchWithAuth('/api/store-settings/staffing-requirements');
      if (response.ok) {
        const data = await response.json();
        setStaffingRequirements(data);
      }
    } catch (error) {
      setError('Σφάλμα κατά τη φόρτωση απαιτήσεων προσωπικού');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Validation
      if (!formData.time_start || !formData.time_end) {
        setError('Παρακαλώ συμπληρώστε όλα τα πεδία');
        setLoading(false);
        return;
      }
      
      if (formData.required_staff < 1) {
        setError('Το απαιτούμενο προσωπικό πρέπει να είναι τουλάχιστον 1');
        setLoading(false);
        return;
      }
      
      const url = editingRequirement 
        ? `/api/store-settings/staffing-requirements/${editingRequirement.id}`
        : '/api/store-settings/staffing-requirements';
      
      const method = editingRequirement ? 'PUT' : 'POST';
      
      console.log('Submitting:', { url, method, formData });
      
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      console.log('Response status:', response.status);
      
      if (response.ok) {
        const result = await response.json();
        console.log('Success result:', result);
        setSuccess('Απαιτήσεις προσωπικού αποθηκεύτηκαν επιτυχώς!');
        setTimeout(() => setSuccess(''), 3000);
        setOpenDialog(false);
        setEditingRequirement(null);
        fetchStaffingRequirements();
        resetForm();
      } else {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        setError(`Σφάλμα κατά την αποθήκευση: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Submit error:', error);
      setError(`Σφάλμα κατά την αποθήκευση: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetchWithAuth(`/api/store-settings/staffing-requirements/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchStaffingRequirements();
        setSuccess('Απαίτηση διαγράφηκε επιτυχώς!');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Σφάλμα κατά τη διαγραφή');
    }
  };

  const resetForm = () => {
    setFormData({
      day_of_week: 1,
      time_period: 'morning',
      time_start: '09:00',
      time_end: '17:00',
      position: 'service',
      required_staff: 1,
      shift_duration_hours: 8
    });
  };

  const openEditDialog = (requirement) => {
    setEditingRequirement(requirement);
    setFormData({ ...requirement });
    setOpenDialog(true);
  };

  const getDayLabel = (dayOfWeek) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayOfWeek);
    return day ? day.label : dayOfWeek;
  };

  const getPeriodLabel = (period) => {
    const p = TIME_PERIODS.find(tp => tp.value === period);
    return p ? p.label : period;
  };

  const getPositionLabel = (position) => {
    const p = POSITIONS.find(pos => pos.value === position);
    return p ? p.label : position;
  };

  if (loading) return <Typography>Φόρτωση...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Απαιτήσεις Προσωπικού
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            resetForm();
            setEditingRequirement(null);
            setOpenDialog(true);
          }}
        >
          Προσθήκη Απαίτησης
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Ημέρα</TableCell>
            <TableCell>Περίοδος</TableCell>
            <TableCell>Ώρες</TableCell>
            <TableCell>Θέση</TableCell>
            <TableCell>Απαιτούμενο Προσωπικό</TableCell>
            <TableCell>Διάρκεια Βάρδιας</TableCell>
            <TableCell>Ενέργειες</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {staffingRequirements.map((req) => (
            <TableRow key={req.id}>
              <TableCell>{getDayLabel(req.day_of_week)}</TableCell>
              <TableCell>
                <Chip label={getPeriodLabel(req.time_period)} size="small" />
              </TableCell>
              <TableCell>{req.time_start} - {req.time_end}</TableCell>
              <TableCell>
                <Chip label={getPositionLabel(req.position)} variant="outlined" size="small" />
              </TableCell>
              <TableCell>{req.required_staff}</TableCell>
              <TableCell>
                <Chip label={`${req.shift_duration_hours || 8}ωρο`} color="primary" variant="outlined" size="small" />
              </TableCell>
              <TableCell>
                <IconButton onClick={() => openEditDialog(req)} size="small">
                  <EditIcon />
                </IconButton>
                <IconButton onClick={() => handleDelete(req.id)} size="small" color="error">
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingRequirement ? 'Επεξεργασία Απαίτησης' : 'Προσθήκη Απαίτησης'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Ημέρα</InputLabel>
                <Select
                  value={formData.day_of_week}
                  onChange={(e) => setFormData({...formData, day_of_week: e.target.value})}
                >
                  {DAYS_OF_WEEK.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Περίοδος</InputLabel>
                <Select
                  value={formData.time_period}
                  onChange={(e) => setFormData({...formData, time_period: e.target.value})}
                >
                  {TIME_PERIODS.map((period) => (
                    <MenuItem key={period.value} value={period.value}>
                      {period.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ώρα Έναρξης"
                type="time"
                value={formData.time_start}
                onChange={(e) => setFormData({...formData, time_start: e.target.value})}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Ώρα Λήξης"
                type="time"
                value={formData.time_end}
                onChange={(e) => setFormData({...formData, time_end: e.target.value})}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Θέση</InputLabel>
                <Select
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                >
                  {POSITIONS.map((position) => (
                    <MenuItem key={position.value} value={position.value}>
                      {position.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Απαιτούμενο Προσωπικό"
                type="number"
                value={formData.required_staff}
                onChange={(e) => setFormData({...formData, required_staff: parseInt(e.target.value)})}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Διάρκεια Βάρδιας</InputLabel>
                <Select
                  value={formData.shift_duration_hours || 8}
                  onChange={(e) => setFormData({...formData, shift_duration_hours: parseInt(e.target.value)})}
                >
                  <MenuItem value={4}>4 ώρες (Μερικής απασχόλησης)</MenuItem>
                  <MenuItem value={5}>5 ώρες (Μερικής απασχόλησης)</MenuItem>
                  <MenuItem value={6}>6 ώρες (Μερικής απασχόλησης)</MenuItem>
                  <MenuItem value={8}>8 ώρες (Πλήρης απασχόληση)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={loading}>
            Ακύρωση
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Αποθήκευση...' : (editingRequirement ? 'Ενημέρωση' : 'Προσθήκη')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// myDATA Integration Settings Component
const MyDataSettings = () => {
  const [mydataSettings, setMydataSettings] = useState({
    mydata_user_id: '',
    mydata_subscription_key: '',
    mydata_company_vat: '',
    mydata_sync_from_date: dayjs('2025-11-01'),
    mydata_last_sync: '',
    taxisnet_username: '',
    taxisnet_password: '',
    gemi_api_key: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadMydataSettings();
  }, []);

  const loadMydataSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/settings');
      
      const userIdRes = await axios.get('/api/settings/mydata_user_id');
      const keyRes = await axios.get('/api/settings/mydata_subscription_key');
      const vatRes = await axios.get('/api/settings/mydata_company_vat');
      const taxisUserRes = await axios.get('/api/settings/taxisnet_username');
      const taxisPassRes = await axios.get('/api/settings/taxisnet_password');
      const gemiKeyRes = await axios.get('/api/settings/gemi_api_key');
      
      // Convert date string to dayjs object
      // Get the actual date value (not the masked encrypted value)
      let syncFromDate = '2025-11-01';
      if (response.data.mydata_sync_from_date && 
          response.data.mydata_sync_from_date !== '***encrypted***' &&
          typeof response.data.mydata_sync_from_date === 'string') {
        syncFromDate = response.data.mydata_sync_from_date;
      }
      
      // Try to get the date from a direct API call if it's not in the bulk response
      try {
        const dateRes = await axios.get('/api/settings/mydata_sync_from_date');
        if (dateRes.data && dateRes.data.value && dateRes.data.value !== '***encrypted***') {
          syncFromDate = dateRes.data.value;
        }
      } catch (e) {
        // Use default if fetch fails
      }
      
      setMydataSettings({
        mydata_user_id: userIdRes.data.value || '',
        mydata_subscription_key: keyRes.data.value || '',
        mydata_company_vat: vatRes.data.value || '',
        mydata_sync_from_date: dayjs(syncFromDate).isValid() ? dayjs(syncFromDate) : dayjs('2025-11-01'),
        mydata_last_sync: response.data.mydata_last_sync || '',
        taxisnet_username: taxisUserRes.data.value || '',
        taxisnet_password: taxisPassRes.data.value || '',
        gemi_api_key: gemiKeyRes.data.value || ''
      });
    } catch (error) {
      console.error('Error loading myDATA settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMydataChange = (field) => (event) => {
    setMydataSettings({
      ...mydataSettings,
      [field]: event.target.value
    });
  };

  const handleDateChange = (newDate) => {
    setMydataSettings({
      ...mydataSettings,
      mydata_sync_from_date: newDate
    });
  };

  const handleSaveMydata = async () => {
    try {
      setSaving(true);
      
      await axios.put('/api/settings/mydata_user_id', {
        value: mydataSettings.mydata_user_id
      });
      
      await axios.put('/api/settings/mydata_subscription_key', {
        value: mydataSettings.mydata_subscription_key
      });
      
      await axios.put('/api/settings/mydata_company_vat', {
        value: mydataSettings.mydata_company_vat
      });
      
      // Convert dayjs object to YYYY-MM-DD string format
      if (!mydataSettings.mydata_sync_from_date) {
        throw new Error('Η ημερομηνία είναι υποχρεωτική');
      }
      
      // Check if it's a valid dayjs object
      let dateString;
      if (typeof mydataSettings.mydata_sync_from_date.format === 'function') {
        if (!mydataSettings.mydata_sync_from_date.isValid()) {
          throw new Error('Μη έγκυρη ημερομηνία');
        }
        dateString = mydataSettings.mydata_sync_from_date.format('YYYY-MM-DD');
      } else if (typeof mydataSettings.mydata_sync_from_date === 'string') {
        // If it's already a string, validate it
        dateString = mydataSettings.mydata_sync_from_date;
        const testDate = dayjs(dateString);
        if (!testDate.isValid()) {
          throw new Error('Μη έγκυρη μορφή ημερομηνίας');
        }
        dateString = testDate.format('YYYY-MM-DD');
      } else {
        throw new Error('Μη έγκυρη ημερομηνία');
      }
      
      await axios.put('/api/settings/mydata_sync_from_date', {
        value: dateString
      });
      
      await axios.put('/api/settings/taxisnet_username', {
        value: mydataSettings.taxisnet_username
      });
      
      await axios.put('/api/settings/taxisnet_password', {
        value: mydataSettings.taxisnet_password
      });
      
      await axios.put('/api/settings/gemi_api_key', {
        value: mydataSettings.gemi_api_key
      });
      
      setSnackbar({
        open: true,
        message: 'Οι ρυθμίσεις myDATA αποθηκεύτηκαν επιτυχώς',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving myDATA settings:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Σφάλμα αποθήκευσης ρυθμίσεων';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          myDATA Integration
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Εισάγετε τα στοιχεία πρόσβασης από το myDATA για αυτόματη εισαγωγή τιμολογίων.
          Τα στοιχεία αποθηκεύονται κρυπτογραφημένα.
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="User ID"
                  value={mydataSettings.mydata_user_id}
                  onChange={handleMydataChange('mydata_user_id')}
                  placeholder="Το User ID σας από το myDATA (π.χ. 123456789)"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Subscription Key"
                  type="password"
                  value={mydataSettings.mydata_subscription_key}
                  onChange={handleMydataChange('mydata_subscription_key')}
                  placeholder="Το Subscription Key σας"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="ΑΦΜ Εταιρείας"
                  value={mydataSettings.mydata_company_vat}
                  onChange={handleMydataChange('mydata_company_vat')}
                  placeholder="123456789"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Ημερομηνία Έναρξης Συγχρονισμού"
                  value={mydataSettings.mydata_sync_from_date}
                  onChange={handleDateChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      helperText: 'Επιλέξτε από ποια ημερομηνία θέλετε να αρχίσει ο συγχρονισμός δεδομένων'
                    }
                  }}
                />
              </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                TAXISnet Credentials (για αναζήτηση ονομάτων προμηθευτών)
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="TAXISnet Username"
                value={mydataSettings.taxisnet_username}
                onChange={handleMydataChange('taxisnet_username')}
                placeholder="Το username TAXISnet"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="TAXISnet Password"
                type="password"
                value={mydataSettings.taxisnet_password}
                onChange={handleMydataChange('taxisnet_password')}
                placeholder="Το password TAXISnet"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                ΓΕΜΗ API Key (για αναζήτηση ονομάτων προμηθευτών - Προτιμάται)
              </Typography>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="ΓΕΜΗ API Key"
                type="password"
                value={mydataSettings.gemi_api_key}
                onChange={handleMydataChange('gemi_api_key')}
                placeholder="Το API Key από το ΓΕΜΗ Open Data API"
                helperText="Το ΓΕΜΗ API είναι REST JSON και προτιμάται έναντι του TAXISnet SOAP API"
              />
            </Grid>
            
            {mydataSettings.mydata_last_sync && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Τελευταίος συγχρονισμός: {new Date(mydataSettings.mydata_last_sync).toLocaleString('el-GR')}
                </Typography>
              </Grid>
            )}
          </Grid>
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveMydata}
              disabled={saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Αποθήκευση'}
            </Button>
          </Box>
        </>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
    </LocalizationProvider>
  );
};

// Main Unified Settings Component
const UnifiedSettings = () => {
  const [tabValue, setTabValue] = useState(0);
  const { presetHours, setPresetHours } = usePresetHours();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Ρυθμίσεις Συστήματος
      </Typography>
      
      <Paper sx={{ width: '100%', mt: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Ωράριο Καταστήματος" />
          <Tab label="Απαιτήσεις Προσωπικού" />
          <Tab label="Θέσεις Εργασίας" />
          <Tab label="Προκαθορισμένες Ώρες" />
          <Tab label="myDATA Integration" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <StoreHoursSettings />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <WeeklyStaffingRequirements />
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <PositionsSettings />
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <PresetHoursSettings initialHours={presetHours} onChange={setPresetHours} />
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <MyDataSettings />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default UnifiedSettings;
